/**
 * Talks to the ei4heidelberg CMS (Payload) about job listings.
 *
 * Every request goes through /cms-api, a reverse proxy that attaches the CMS API
 * key server-side (setupProxy.js in dev, Nginx in production). The key must never
 * be shipped to the browser — it authenticates as a real CMS user.
 */

const API_BASE = (process.env.REACT_APP_CMS_API_URL || '/cms-api').replace(/\/$/, '');

/** Public CMS origin, used only to build links a human clicks. */
const CMS_PUBLIC_URL = (process.env.REACT_APP_CMS_PUBLIC_URL || 'https://cms.eihub.de').replace(/\/$/, '');

function describeHttpError(status) {
    if (status === 401 || status === 403) {
        return 'Keine Berechtigung — der CMS-API-Schlüssel fehlt oder ist ungültig.';
    }
    if (status === 413) return 'Upload zu groß (max. 20 MB).';
    if (status === 404) return 'CMS-Endpunkt nicht gefunden — läuft der Proxy?';
    return `CMS antwortete mit HTTP ${status}.`;
}

/** The error message of an already parsed response body, falling back to the status. */
function messageFromBody(body, status) {
    if (body?.error) return body.error;
    if (Array.isArray(body?.errors) && body.errors[0]?.message) return body.errors[0].message;
    return describeHttpError(status);
}

/** Pull the error message out of a failed response, falling back to the status. */
async function readError(response) {
    try {
        return messageFromBody(await response.json(), response.status);
    } catch {
        // non-JSON body (proxy error page) — fall through
        return describeHttpError(response.status);
    }
}

/** Absolute URL for an asset the CMS reports relative to its own origin. */
function absoluteCmsUrl(url) {
    if (!url) return null;
    return /^https?:\/\//i.test(url) ? url : `${CMS_PUBLIC_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * The proxy path for a URL on the CMS API, e.g. a campaign page file.
 *
 * Fetching the CMS directly fails in the browser: its CORS list only admits
 * the CMS and the website. Through /cms-api the request is same-origin.
 */
export function cmsProxyUrl(url) {
    const match = /^(?:https?:\/\/[^/]+)?\/api(\/.*)$/i.exec(url || '');
    return match ? `${API_BASE}${match[1]}` : url;
}

/** Flatten one CMS document into what the cards actually render. */
function normalizeJob(doc) {
    const campaignUrl = absoluteCmsUrl(doc?.campaignPage?.url);
    const isPublished = doc?._status === 'published';

    return {
        id: doc.id,
        title: doc.title || '(ohne Titel)',
        slug: doc.slug || null,
        role: doc.role || null,
        department: doc?.department?.name || null,
        departmentId: doc?.department?.id || null,
        status: isPublished ? 'published' : 'draft',
        datePosted: doc.datePosted || null,
        validThrough: doc.validThrough || null,
        updatedAt: doc.updatedAt || null,
        campaignUrl,
        campaignFilename: doc?.campaignPage?.filename || null,
        indeedUrl: doc.indeedUrl || null,
        adminUrl: `${CMS_PUBLIC_URL}/admin/collections/job-listings/${doc.id}`,
    };
}

/**
 * URL segment for a new department, mirroring the CMS's own slugify:
 * German characters are kept, other diacritics reduced, everything else a hyphen.
 * Kept in sync with payload/src/utilities/slugify.ts.
 */
export function slugifyDepartment(text) {
    const GERMAN_CHARS = /([äöüß])/;
    return text
        .toLowerCase()
        .split(GERMAN_CHARS)
        .map((part) => (GERMAN_CHARS.test(part) ? part : part.normalize('NFD').replace(/[̀-ͯ]/g, '')))
        .join('')
        .replace(/[^a-z0-9äöüß]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

async function postImport(formData) {
    try {
        const response = await fetch(`${API_BASE}/job-listings/hr-import`, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            return { success: false, error: await readError(response) };
        }

        const body = await response.json();
        return { success: true, data: body };
    } catch (error) {
        return { success: false, error: `Upload fehlgeschlagen: ${error.message}` };
    }
}

class JobListingsService {
    /** All job listings, drafts included, newest change first. */
    async getJobListings() {
        try {
            const response = await fetch(
                `${API_BASE}/job-listings?depth=1&draft=true&limit=200&sort=-updatedAt`,
                { headers: { Accept: 'application/json' } }
            );

            if (!response.ok) {
                return { success: false, error: await readError(response) };
            }

            const body = await response.json();
            return { success: true, data: (body?.docs || []).map(normalizeJob) };
        } catch (error) {
            return { success: false, error: `Verbindung zum CMS fehlgeschlagen: ${error.message}` };
        }
    }

    /**
     * Whether the proxy is attaching a valid CMS key.
     *
     * Worth knowing before anything else: reading job listings works without one
     * (the CMS serves published documents to anyone), so the page looks healthy
     * while drafts are silently filtered out and every write returns 401/403.
     */
    async getAuthState() {
        try {
            // Deliberately not /users/me: that returns the user document, and
            // the CMS user has useAPIKey enabled, so the response carries the
            // shared API key in plaintext. This endpoint returns a boolean.
            const response = await fetch(`${API_BASE}/job-listings/auth-check`, {
                headers: { Accept: 'application/json' },
            });
            // 404 means the endpoint is not deployed yet, which is not the same
            // as "no key". Answering `false` there would lock the page for a
            // reason that has nothing to do with credentials, so say "unknown"
            // and let a real write report its own error.
            if (response.status === 404) return { success: true, data: { authenticated: null } };
            if (!response.ok) return { success: true, data: { authenticated: false } };

            const body = await response.json();
            return { success: true, data: { authenticated: Boolean(body?.authenticated) } };
        } catch {
            return { success: true, data: { authenticated: null } };
        }
    }

    /** All departments (Bereiche), alphabetical. */
    async getDepartments() {
        try {
            const response = await fetch(`${API_BASE}/departments?limit=200&sort=name`, {
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) return { success: false, error: await readError(response) };

            const body = await response.json();
            return {
                success: true,
                data: (body?.docs || []).map((doc) => ({ id: doc.id, name: doc.name, slug: doc.slug })),
            };
        } catch (error) {
            return { success: false, error: `Bereiche konnten nicht geladen werden: ${error.message}` };
        }
    }

    /** Create a department. The CMS requires a slug and fills none itself. */
    async createDepartment(name) {
        const trimmed = name.trim();
        const slug = slugifyDepartment(trimmed);
        if (!trimmed || !slug) {
            return { success: false, error: 'Bitte einen Namen eingeben.' };
        }

        try {
            const response = await fetch(`${API_BASE}/departments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ name: trimmed, slug }),
            });
            if (!response.ok) return { success: false, error: await readError(response) };

            const body = await response.json();
            const doc = body?.doc || body;
            return { success: true, data: { id: doc.id, name: doc.name, slug: doc.slug } };
        } catch (error) {
            return { success: false, error: `Bereich anlegen fehlgeschlagen: ${error.message}` };
        }
    }

    /**
     * Set (or clear) a job's department.
     *
     * Drafts are patched with ?draft=true — without it Payload would write to the
     * published version and push an unpublished listing live.
     */
    async setJobDepartment(jobId, departmentId, { isDraft }) {
        const query = isDraft ? '?draft=true' : '';
        try {
            const response = await fetch(`${API_BASE}/job-listings/${jobId}${query}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ department: departmentId || null }),
            });
            if (!response.ok) return { success: false, error: await readError(response) };
            return { success: true, data: await response.json() };
        } catch (error) {
            return { success: false, error: `Bereich speichern fehlgeschlagen: ${error.message}` };
        }
    }

    /**
     * Put a job's newest draft live, the same request as "Veröffentlichen" in the
     * CMS admin. ?draft=true makes Payload start from the newest draft rather than
     * the last published version, and `_status: 'published'` turns it live.
     */
    async publishJob(jobId) {
        try {
            const response = await fetch(`${API_BASE}/job-listings/${jobId}?draft=true&depth=0`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ _status: 'published' }),
            });
            if (!response.ok) return { success: false, error: await readError(response) };
            return { success: true };
        } catch (error) {
            return { success: false, error: `Veröffentlichen fehlgeschlagen: ${error.message}` };
        }
    }

    /**
     * Take a live job off the website, the same request as "Unpublish" in the CMS
     * admin. Deliberately without ?draft=true: that would only add a draft on top
     * and leave the published version, and so the job, online.
     */
    async unpublishJob(jobId) {
        try {
            const response = await fetch(`${API_BASE}/job-listings/${jobId}?depth=0`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ _status: 'draft' }),
            });
            if (!response.ok) return { success: false, error: await readError(response) };
            return { success: true };
        } catch (error) {
            return { success: false, error: `Deaktivieren fehlgeschlagen: ${error.message}` };
        }
    }

    /**
     * Publish several drafts in one request, like the CMS admin's bulk action.
     *
     * Payload keeps going when one job fails validation and answers 400 with both
     * `docs` (published) and `errors` (not published), so a 400 carrying `docs` is a
     * partial success, not a failure.
     */
    async publishJobs(jobIds) {
        if (jobIds.length === 0) return { success: true, data: { published: 0, failed: [] } };

        const params = new URLSearchParams({
            draft: 'true',
            depth: '0',
            'select[id]': 'true',
            'where[and][0][id][in]': jobIds.join(','),
            'where[and][1][_status][not_equals]': 'published',
        });
        try {
            const response = await fetch(`${API_BASE}/job-listings?${params}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ _status: 'published' }),
            });
            const body = await response.json().catch(() => null);
            if (!Array.isArray(body?.docs)) {
                return { success: false, error: messageFromBody(body, response.status) };
            }
            return {
                success: true,
                data: {
                    published: body.docs.length,
                    failed: (body.errors || []).map((e) => ({ id: e.id, message: e.message })),
                },
            };
        } catch (error) {
            return { success: false, error: `Veröffentlichen fehlgeschlagen: ${error.message}` };
        }
    }

    /** Upload one ZIP holding any number of HTML+JSON pairs. */
    async importZip(zipFile) {
        const formData = new FormData();
        formData.append('file', zipFile, zipFile.name);
        return postImport(formData);
    }

    /** Upload exactly one pair as two separate files. */
    async importPair(htmlFile, jsonFile) {
        const formData = new FormData();
        formData.append('html', htmlFile, htmlFile.name);
        formData.append('json', jsonFile, jsonFile.name);
        return postImport(formData);
    }
}

export const jobListingsService = new JobListingsService();
export default jobListingsService;
